import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { AttentionList } from "../components/dashboard/AttentionList";
import { Card } from "../components/dashboard/Card";
import { FleetPanel } from "../components/dashboard/FleetPanel";
import { MoneyPanel } from "../components/dashboard/MoneyPanel";
import { UsagePanel } from "../components/dashboard/UsagePanel";
import { PageHeader } from "../components/shell/PageHeader";
import { Combobox } from "../components/ui/Combobox";
import { ErrorNotice } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { Metric, SectionTitle } from "../components/ui/Text";
import { useAlertSummary } from "../lib/alerts";
import { get, getPage } from "../lib/api";
import {
  DASHBOARD_RANGES,
  DASHBOARD_TIME_ZONE,
  attentionItems,
  readDashboardRange,
  resolveDashboardWindow,
} from "../lib/dashboard";
import { count, kwh } from "../lib/format";
import { useSession } from "../lib/session";
import type { Depot, FleetStats, PublicStats, UsageStats } from "../lib/types";

/**
 * Overview — the page a shift starts from.
 *
 * ## Three requests, drawn independently
 *
 * Each panel is its own `/stats/*` call behind its own permission, so a support
 * role that may see orders and not stations gets the money panel and nothing
 * pretending to be a fleet. A panel the principal may not read is not rendered at
 * all rather than rendered as a 403: it is not broken, it is not theirs.
 *
 * ## What polls, and how fast
 *
 * Everything, every thirty seconds — the fleet is live state, and the windowed
 * figures include "today", which moves while the page is open. Thirty because
 * the alert sweeper ticks at thirty and nothing here changes meaningfully faster;
 * a panel left on a wall all day costs three small requests a tick.
 *
 * ## The period is one zone's days
 *
 * "Today" is Phnom Penh's today, for every operator, because the server draws the
 * daily series in that zone (`routes/stats.ts`). The zone is printed beside the
 * period so a reader elsewhere knows whose midnight it is.
 */

const REFRESH_MS = 30_000;

export function Dashboard() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const range = readDashboardRange(params.get("range"));
  const requestedDepot = params.get("depotId") ?? "";
  const period = resolveDashboardWindow(range, DASHBOARD_TIME_ZONE);
  const from = period.from.toISOString();
  const to = period.to.toISOString();

  const canFleet = can("charge_points.view");
  const canUsage = can("sessions.view");
  const canMoney = can("public_orders.view");

  /** Only the usage panel narrows by site, so the filter is offered only with it. */
  const depots = useQuery({
    queryKey: ["depots"],
    queryFn: () => getPage<Depot>("/depots"),
    enabled: canUsage && can("depots.view"),
  });

  /**
   * The filter in effect is one the operator can see. A `?depotId` from a pasted
   * link, for a principal who cannot list depots or a site that has since gone,
   * would otherwise narrow every charging figure behind a control that is not drawn.
   */
  const depotId =
    depots.data?.rows.some((depot) => depot.id === requestedDepot) === true ? requestedDepot : "";

  const fleet = useQuery({
    queryKey: ["stats", "fleet"],
    queryFn: () => get<FleetStats>("/stats/fleet"),
    enabled: canFleet,
    refetchInterval: REFRESH_MS,
  });

  const usage = useQuery({
    queryKey: ["stats", "usage", { from, to, depotId }],
    queryFn: () => get<UsageStats>("/stats/usage", { query: { from, to, depotId } }),
    enabled: canUsage,
    refetchInterval: REFRESH_MS,
    // Changing the period keeps the old figures up until the new ones land,
    // rather than collapsing every panel to a placeholder for a round trip.
    placeholderData: (previous) => previous,
  });

  const money = useQuery({
    queryKey: ["stats", "public", { from, to }],
    queryFn: () => get<PublicStats>("/stats/public", { query: { from, to } }),
    enabled: canMoney,
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });

  const alerts = useAlertSummary();

  const live = [
    ...(canFleet ? [fleet] : []),
    ...(canUsage ? [usage] : []),
    ...(canMoney ? [money] : []),
  ];
  // The oldest of them: the caption is about the stalest figure on the page.
  const updatedAt = live.length === 0 ? 0 : Math.min(...live.map((query) => query.dataUpdatedAt));

  const items = attentionItems({
    fleet: fleet.data,
    publicStats: money.data,
    alerts: alerts.data,
    now: new Date(),
  });
  const attentionSources = [
    ...(canFleet ? [fleet] : []),
    ...(canMoney ? [money] : []),
    ...(can("alerts.view") ? [alerts] : []),
  ];
  const isAttentionComplete = attentionSources.every((query) => query.isSuccess);
  const hasAttentionFailure = attentionSources.some((query) => query.isError);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "" || (key === "range" && value === "today")) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const critical = alerts.data?.open.critical;

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Overview"
        freshness={
          live.length === 0 ? undefined : (
            <Freshness
              updatedAt={updatedAt}
              isFetching={live.some((query) => query.isFetching)}
              intervalMs={REFRESH_MS}
            />
          )
        }
        metrics={
          <>
            {!canFleet ? null : (
              <Metric
                label="Online"
                value={count(fleet.data?.stations.online)}
                {...(fleet.data === undefined
                  ? {}
                  : { unit: `of ${count(fleet.data.stations.accepted)}` })}
                hint="accepted stations"
              />
            )}
            {!canUsage ? null : (
              <>
                <Metric
                  label="Charging now"
                  value={count(usage.data?.activeSessions)}
                  hint="active sessions"
                />
                <Metric
                  label="Energy"
                  value={kwh(usage.data?.totals.energyWh)}
                  unit="kWh"
                  hint={`${period.label} · ${count(usage.data?.totals.sessions)} sessions`}
                />
              </>
            )}
            {!can("alerts.view") ? null : (
              <Metric
                label="Critical"
                value={count(critical)}
                hint="open alerts"
                tone={(critical ?? 0) > 0 ? "signal" : "muted"}
              />
            )}
          </>
        }
        lede={`Charging and money count whole days in ${DASHBOARD_TIME_ZONE}. Fleet state, alerts and the money owed are as of now.`}
      />

      <div className="flex flex-col gap-6 pb-section">
        {/* Charging leads: the fleet's own cards and public drivers side by side in
            one card, under the window that both of them count. */}
        {!canUsage && !canMoney ? null : (
          <Card>
            {/* The filters live on the card they filter: Fleet and Needs attention are
                "as of now" and ignore both. Two chips rather than a form, because on a
                page read at a glance they are settings, not the subject. */}
            <div
              role="group"
              aria-label="Reporting window"
              className="mb-6 flex flex-wrap items-center gap-2"
            >
              <Combobox
                label="Window"
                icon={<CalendarGlyph />}
                value={range}
                options={DASHBOARD_RANGES}
                onChange={(next) => setParam("range", next)}
              />
              {depots.data === undefined ? null : (
                <Combobox
                  label="Depot"
                  icon={<PinGlyph />}
                  value={depotId}
                  options={[
                    { value: "", label: "All depots" },
                    ...depots.data.rows.map((depot) => ({ value: depot.id, label: depot.name })),
                  ]}
                  searchPlaceholder="Search depots"
                  onChange={(next) => setParam("depotId", next)}
                />
              )}
              {depots.data === undefined ? null : (
                <span className="text-micro text-ink-faint">
                  Depot narrows Charging, not Public charging
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-y-section lg:grid-cols-2">
              {!canUsage ? null : (
                <div className={canMoney ? "lg:border-r lg:border-rule lg:pr-8" : "lg:col-span-2"}>
                  <Panel title="Charging" query={usage}>
                    {(data) => <UsagePanel usage={data} label={period.label} />}
                  </Panel>
                </div>
              )}
              {!canMoney ? null : (
                <div
                  className={
                    canUsage
                      ? "border-t border-rule pt-section lg:border-t-0 lg:pt-0 lg:pl-8"
                      : "lg:col-span-2"
                  }
                >
                  <Panel title="Public charging" query={money}>
                    {(data) => <MoneyPanel stats={data} label={period.label} />}
                  </Panel>
                </div>
              )}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Card className={canFleet ? "lg:col-span-7" : "lg:col-span-12"}>
            <AttentionList
              items={items}
              isComplete={isAttentionComplete}
              hasFailure={hasAttentionFailure}
            />
          </Card>

          {!canFleet ? null : (
            <Card className="lg:col-span-5">
              <Panel title="Fleet" query={fleet}>
                {(data) => <FleetPanel fleet={data} zone={DASHBOARD_TIME_ZONE} />}
              </Panel>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * A panel's three states. The data once it is in; until then its title over a
 * still placeholder of roughly the panel's height, so nothing jumps when it lands;
 * and a failure in place of the panel, not the page — one endpoint failing is no
 * reason to hide the other two.
 */
function Panel<T>({
  title,
  query,
  children,
}: {
  readonly title: string;
  readonly query: {
    readonly data: T | undefined;
    readonly error: unknown;
    readonly refetch: () => unknown;
  };
  readonly children: (data: T) => ReactNode;
}) {
  const titleId = useId();

  if (query.data !== undefined) return children(query.data);

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <SectionTitle>
        <span id={titleId}>{title}</span>
      </SectionTitle>
      {query.error === null ? (
        <div aria-hidden="true" className="flex flex-col gap-3">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className="stage block h-10 bg-paper-sunken"
              style={{ ["--stage-index" as string]: index }}
            />
          ))}
        </div>
      ) : (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      )}
    </section>
  );
}

function CalendarGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <rect
        x="2.5"
        y="3.5"
        width="11"
        height="10"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M2.5 6.5h11M5.5 2v3M10.5 2v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 0 0-9 0C3.5 9.8 8 14 8 14Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="8" cy="6.5" r="1.6" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
